import { useMemo, useState } from "react";
import { libraryStories } from "@/mocks/chiguaData";
import type { TwistStory } from "@/mocks/chiguaData";
import { formatViews } from "@/utils/format";
import SectionHeading from "./SectionHeading";
import TagBadge from "./TagBadge";

const tabs = ["全部", "职场反转", "感情瓜田", "邻里奇闻", "家庭伦理", "校园热搜", "财富逆转"];

export default function TwistLibrary() {
  const [active, setActive] = useState("全部");
  const [revealed, setRevealed] = useState<number[]>([]);

  const list = useMemo(
    () => (active === "全部" ? libraryStories : libraryStories.filter((s) => s.category === active)),
    [active],
  );

  const toggleReveal = (id: number) => {
    setRevealed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <section id="library" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          kicker="反转现场"
          title="先猜结局，再揭晓"
          desc="结局先替你憋着。看完悬念开头自己先脑补一个结尾，再点「揭晓」，体验被现实结尾打脸的快乐。"
        />

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setActive(t);
                setRevealed([]);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === t
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-primary-600"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-5">
          {list.map((s, idx) => (
            <StoryRow key={s.id} story={s} index={idx + 1} isRevealed={revealed.includes(s.id)} onToggle={() => toggleReveal(s.id)} />
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
          >
            <i className="ri-add-line" aria-hidden="true"></i>
            还有更多，去投稿补瓜
          </button>
        </div>
      </div>
    </section>
  );
}

interface StoryRowProps {
  story: TwistStory;
  index: number;
  isRevealed: boolean;
  onToggle: () => void;
}

function StoryRow({ story, index, isRevealed, onToggle }: StoryRowProps) {
  return (
    <article className="rounded-3xl border border-background-200/70 bg-background-100/50 p-5 md:p-6">
      <div className="flex items-start gap-4 md:gap-5">
        <span className="hidden font-heading text-3xl font-black text-secondary-300 sm:block md:text-5xl">
          {String(index).padStart(2, "0")}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <TagBadge tag={story.tag} />
            <span className="text-xs text-foreground-500">{story.category}</span>
          </div>

          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-2xl">
            {story.title}
          </h3>

          <div className={`mt-3 rounded-2xl border-l-4 ${isRevealed ? "border-accent-400" : "border-secondary-300"} bg-background-50/80 p-4`}>
            <p className="text-xs font-semibold tracking-wide text-secondary-700">
              <i className="ri-question-line mr-1.5" aria-hidden="true"></i>
              悬念开场
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-700 md:text-base">{story.hook}</p>

            {!isRevealed ? (
              <button
                type="button"
                onClick={onToggle}
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-eye-off-line" aria-hidden="true"></i>
                揭晓结局（慎点）
              </button>
            ) : (
              <div className="mt-4 rounded-xl bg-accent-100/80 p-4">
                <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-accent-700">
                  <i className="ri-flashlight-fill" aria-hidden="true"></i>
                  结局反转
                </p>
                <p className="mt-1.5 text-sm font-medium leading-relaxed text-foreground-950 md:text-base">
                  {story.twist}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-smile-line text-sm" aria-hidden="true"></i>
              {story.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line text-sm" aria-hidden="true"></i>
              {story.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line text-sm" aria-hidden="true"></i>
              {formatViews(story.views)} 围观
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-heart-3-line text-sm text-primary-500" aria-hidden="true"></i>
              {formatViews(story.likes)} 赞
            </span>
            {!isRevealed && (
              <button
                type="button"
                onClick={onToggle}
                className="ml-auto inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700"
              >
                我先猜一个
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}